import React, { useEffect, useState } from 'react';
import { payoutBatches, adminBonuses, bonuses, PAYING_COMPANY_OPTIONS } from './api';
import { OneTimeBonusDetail, OneTimeBonusLines } from './OneTimeBonuses';
import { mergeTimeline, pageSlice } from './payoutTimeline';
import { CarryInfo, CarryNotice, PartsPanel, SplitEditor } from './PayoutSplit';
import { blankPart, carryText, paidLabel as batchPaidLabel, partsPayload, splitState, toCents } from './payoutSplit';

// Match the formatting helpers used in Bonuses.jsx. Kept local so this file
// stays self-contained and doesn't require exports from Bonuses.jsx.
const fmtMoney = (v) => {
  const n = Number(v || 0);
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};
const fmtDate = (d) => {
  if (!d) return '—';
  const m = String(d).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString();
  return new Date(d).toLocaleDateString();
};
const fmtDateTime = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleString();
  } catch {
    return String(d);
  }
};

// Matches backend.models.models.BonusPaymentMethod
const PAYMENT_METHODS = ['check', 'zelle', 'venmo', 'cash', 'other'];

// v5.9.1 paid list: fetch every paid batch in chunks, page in the browser.
const PAID_FETCH_CHUNK = 200;
const PAID_PAGE_SIZE = 20;

// Mirror of openPrintUrl in Bonuses.jsx — fetch with bearer token then open as a blob.
async function openPrintUrl(url) {
  try {
    const token = localStorage.getItem('token');
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) {
      alert('Print failed (HTTP ' + res.status + ')');
      return;
    }
    const html = await res.text();
    const blob = new Blob([html], { type: 'text/html' });
    const blobUrl = window.URL.createObjectURL(blob);
    window.open(blobUrl, '_blank');
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
  } catch (e) {
    alert('Print failed: ' + e.message);
  }
}

const STATUS_COLORS = {
  draft:     { bg: '#3a3a3a', color: '#ccc' },
  finalized: { bg: '#185FA5', color: '#fff' },
  reviewed:  { bg: '#3B6D11', color: '#cfe9b6' },
  paid:      { bg: '#1a1a2e', color: '#888' },
  voided:    { bg: '#5a1a1a', color: '#e0a8a8' },
};

const BATCH_STATUS_COLORS = {
  open: { bg: '#3a2a14', color: '#f5c97a' },
  paid: { bg: '#1a3a1a', color: '#a8d8a8' },
};

function extractErrorMessage(e) {
  const detail = e?.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail.map(d => d.msg || JSON.stringify(d)).join('; ');
  }
  return detail || e?.message || 'Unknown error';
}

export default function PayoutBatches({
  user, darkMode,
  bg, panelBg, borderColor, textPrimary, textSecondary,
  selectedBatchId, setSelectedBatchId,
}) {
  const [openBatches, setOpenBatches] = useState([]);
  // v5.9.1: the paid list is ALL paid batches merged with one-time bonuses
  // (display rows only) and paged in the browser — see payoutTimeline.js.
  const [paidBatches, setPaidBatches] = useState([]);
  const [oneOffs, setOneOffs] = useState([]);
  const [oneOffWarning, setOneOffWarning] = useState('');
  const [paidPage, setPaidPage] = useState(0);
  const [detailOneOff, setDetailOneOff] = useState(null);
  // v5.11.0: one-time payments expanded to their lines in the paid list.
  const [expandedOneOffs, setExpandedOneOffs] = useState(() => new Set());
  const [loading, setLoading] = useState(true);

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailMsg, setDetailMsg] = useState('');

  const [showMarkPaid, setShowMarkPaid] = useState(false);
  // v5.9.0: paying_company is required and has NO default — blank until the
  // admin picks one (reset every time the form opens).
  // v5.49.0 amount = what was actually paid (single company; prefilled with the total), reason = why it differs
  const EMPTY_MARK_PAID = { payment_method: 'check', payment_reference: '', notes: '', paying_company: '', amount: '', reason: '' };
  const [markPaidForm, setMarkPaidForm] = useState(EMPTY_MARK_PAID);
  const [markingPaid, setMarkingPaid] = useState(false);
  // v5.45.0 "Split payment": part rows instead of one paying company (admin only, like Mark paid).
  const [splitMode, setSplitMode] = useState(false);
  const [splitRows, setSplitRows] = useState([]);

  // v4.6 batch-level adjustments (manager/admin, open batches only)
  const [showAddAdj, setShowAddAdj] = useState(false);
  const [adjForm, setAdjForm] = useState({ amount: '', note: '' });
  const [savingAdj, setSavingAdj] = useState(false);
  const [editAdjId, setEditAdjId] = useState(null);
  const [editAdjForm, setEditAdjForm] = useState({ amount: '', note: '' });

  const isAdmin = user?.role === 'admin';
  const isAdminOrManager = isAdmin || user?.role === 'manager';

  // Every paid batch, fetched in chunks until a short chunk comes back.
  const loadAllPaidBatches = async () => {
    const all = [];
    for (let offset = 0; ; offset += PAID_FETCH_CHUNK) {
      const res = await payoutBatches.listArchived(PAID_FETCH_CHUNK, offset);
      const chunk = res.data || [];
      all.push(...chunk);
      if (chunk.length < PAID_FETCH_CHUNK) return all;
    }
  };

  // One-time bonuses shown on this page: admin -> all (voided included);
  // any other user who receives bonuses (staff, or a manager who does) ->
  // their own non-voided ones; everyone else (e.g. a manager who doesn't) -> none.
  const loadOneOffs = async () => {
    if (isAdmin) return (await adminBonuses.oneTime.list({ include_voided: true })).data || [];
    if (user?.receives_bonuses) return (await bonuses.mineOneTime()).data || [];
    return [];
  };

  const loadLists = async () => {
    setLoading(true);
    // One-offs load alongside; a failure there must not hide the batches.
    const oneOffsP = loadOneOffs().then(d => ({ ok: true, d }), e => ({ ok: false, e }));
    try {
      const [openRes, paid] = await Promise.all([payoutBatches.listOpen(), loadAllPaidBatches()]);
      setOpenBatches(openRes.data || []);
      setPaidBatches(paid);
    } catch (e) {
      alert('Failed to load batches: ' + extractErrorMessage(e));
    }
    const oneOffRes = await oneOffsP;
    if (oneOffRes.ok) {
      setOneOffs(oneOffRes.d);
      setOneOffWarning('');
    } else {
      setOneOffs([]);
      setOneOffWarning('One-time bonuses could not be loaded: ' + extractErrorMessage(oneOffRes.e));
    }
    setLoading(false);
  };

  const loadDetail = async (batchId) => {
    setDetailLoading(true);
    try {
      const res = await payoutBatches.getDetail(batchId);
      setDetail(res.data);
    } catch (e) {
      alert('Failed to load batch: ' + extractErrorMessage(e));
      setDetail(null);
      setSelectedBatchId && setSelectedBatchId(null);
    }
    setDetailLoading(false);
  };

  useEffect(() => {
    loadLists();
    // eslint-disable-next-line
  }, []);

  // Honor a pre-selected batch id passed in from MyBonuses chip click
  useEffect(() => {
    if (selectedBatchId) {
      loadDetail(selectedBatchId);
    } else {
      setDetail(null);
    }
    // eslint-disable-next-line
  }, [selectedBatchId]);

  const openDetail = (id) => {
    setSelectedBatchId && setSelectedBatchId(id);
    if (!selectedBatchId || selectedBatchId !== id) {
      // useEffect above will fire on the state change. If parent didn't
      // give us setSelectedBatchId (defensive), load directly.
      if (!setSelectedBatchId) loadDetail(id);
    }
  };

  const backToList = () => {
    setSelectedBatchId && setSelectedBatchId(null);
    setDetail(null);
    setShowMarkPaid(false);
    setDetailMsg('');
    loadLists();
  };

  const submitMarkPaid = async () => {
    if (!detail) return;
    const regular = detail.kind !== 'renewal_bonus';
    if (splitMode) {
      const st = splitState(splitRows, detail.total_amount, { allowDiff: regular });
      if (!st.ready) return;
      if (st.diffC && !markPaidForm.reason.trim()) { alert('A reason is required when the payment differs from what is owed.'); return; }
      const delayed = splitRows.filter(r => r.delayed).length;
      if (!window.confirm(`Mark batch #${detail.id} paid in ${splitRows.length} parts${delayed ? ` (${delayed} delayed)` : ''}? This will set all ${detail.entries.length} entries to status=paid and auto-file PDFs.${st.diffC ? `\n\n${carryText(st.diffC, detail.closed_by_name)}.` : ''}`)) return;
      setMarkingPaid(true);
      try {
        await payoutBatches.markPaid(detail.id, {
          payment_method: markPaidForm.payment_method || null,
          payment_reference: markPaidForm.payment_reference || null,
          notes: markPaidForm.notes || null,
          parts: partsPayload(splitRows),
          ...(st.diffC ? { carryover_reason: markPaidForm.reason.trim() } : {}),
        });
        setDetailMsg(`Batch #${detail.id} marked paid in ${splitRows.length} parts.`);
        setTimeout(() => setDetailMsg(''), 6000);
        setShowMarkPaid(false); setSplitMode(false); setSplitRows([]);
        setMarkPaidForm(EMPTY_MARK_PAID);
        await Promise.all([loadDetail(detail.id), loadLists()]);
      } catch (e) {
        alert('Mark paid failed: ' + extractErrorMessage(e));
      }
      setMarkingPaid(false);
      return;
    }
    if (!markPaidForm.payment_method) {
      alert('Pick a payment method');
      return;
    }
    if (!markPaidForm.paying_company) {
      alert('Pick the paying company');
      return;
    }
    const companyLabel = (PAYING_COMPANY_OPTIONS.find(c => c.value === markPaidForm.paying_company) || {}).label;
    const amtC = regular ? toCents(markPaidForm.amount) : toCents(detail.total_amount);
    const diffC = toCents(detail.total_amount) - amtC;
    if (Number.isNaN(amtC) || amtC < 0) { alert('Enter the amount paid'); return; }
    if (diffC && !markPaidForm.reason.trim()) { alert('A reason is required when the payment differs from what is owed.'); return; }
    if (!window.confirm(`Mark batch #${detail.id} paid by ${companyLabel}? This will set all ${detail.entries.length} entries to status=paid and auto-file PDFs.${diffC ? `\n\n${carryText(diffC, detail.closed_by_name)}.` : ''}${amtC === 0 ? '\n\n$0 batch — nothing goes to Books.' : ''}`)) return;
    setMarkingPaid(true);
    try {
      const payload = {
        payment_method: markPaidForm.payment_method,
        payment_reference: markPaidForm.payment_reference || null,
        notes: markPaidForm.notes || null,
        paying_company: markPaidForm.paying_company,
        ...(regular ? { amount: (amtC / 100).toFixed(2) } : {}),
        ...(diffC ? { carryover_reason: markPaidForm.reason.trim() } : {}),
      };
      await payoutBatches.markPaid(detail.id, payload);
      setDetailMsg(`Batch #${detail.id} marked paid.`);
      setTimeout(() => setDetailMsg(''), 6000);
      setShowMarkPaid(false);
      setMarkPaidForm(EMPTY_MARK_PAID);
      // Refresh both detail (status flips to 'paid') and the overview lists
      // (this batch moves from open -> archived).
      await Promise.all([loadDetail(detail.id), loadLists()]);
    } catch (e) {
      alert('Mark paid failed: ' + extractErrorMessage(e));
    }
    setMarkingPaid(false);
  };

  const returnEntryToDraft = async (entryId) => {
    if (!window.confirm('Return this bonus to draft and remove from the open payout batch?')) return;
    try {
      await payoutBatches.returnEntryToDraft(entryId);
      setDetailMsg('Entry returned to draft.');
      setTimeout(() => setDetailMsg(''), 4000);
      // Refresh both detail (entry disappears, counts drop) and the overview
      // lists (entry_count / total_amount on the batch card change).
      await Promise.all([loadDetail(detail.id), loadLists()]);
    } catch (e) {
      alert('Return to draft failed: ' + extractErrorMessage(e));
    }
  };

  // --- Adjustments ---------------------------------------------------------
  // After every mutation we re-fetch detail (total_amount / entry_count come
  // from the backend, which sums net_total over all entries incl. adjustments)
  // and the overview lists, so the batch total updates live.
  const submitAddAdjustment = async () => {
    const amt = parseFloat(adjForm.amount);
    if (!amt || isNaN(amt)) { alert('Enter a non-zero amount (e.g. 50 or -25.50).'); return; }
    if (!adjForm.note.trim()) { alert('A note is required.'); return; }
    setSavingAdj(true);
    try {
      await payoutBatches.addAdjustment(detail.id, { amount: amt, note: adjForm.note.trim() });
      setAdjForm({ amount: '', note: '' });
      setShowAddAdj(false);
      setDetailMsg('Adjustment added.');
      setTimeout(() => setDetailMsg(''), 4000);
      await Promise.all([loadDetail(detail.id), loadLists()]);
    } catch (e) {
      alert('Add adjustment failed: ' + extractErrorMessage(e));
    }
    setSavingAdj(false);
  };

  const startEditAdj = (b) => {
    setEditAdjId(b.id);
    setEditAdjForm({ amount: String(b.net_total_calculated), note: b.notes || '' });
  };

  const submitEditAdjustment = async (entryId) => {
    const amt = parseFloat(editAdjForm.amount);
    if (!amt || isNaN(amt)) { alert('Enter a non-zero amount.'); return; }
    if (!editAdjForm.note.trim()) { alert('A note is required.'); return; }
    try {
      await payoutBatches.editAdjustment(entryId, { amount: amt, note: editAdjForm.note.trim() });
      setEditAdjId(null);
      setDetailMsg('Adjustment updated.');
      setTimeout(() => setDetailMsg(''), 4000);
      await Promise.all([loadDetail(detail.id), loadLists()]);
    } catch (e) {
      alert('Edit adjustment failed: ' + extractErrorMessage(e));
    }
  };

  const removeAdjustment = async (entryId, isCarry = false) => {
    if (!window.confirm(isCarry
      ? 'Remove this carry-over line from the batch? The carry-over goes back to waiting for the next payout.'
      : 'Delete this adjustment from the batch? This cannot be undone.')) return;
    try {
      await payoutBatches.deleteAdjustment(entryId);
      setDetailMsg(isCarry ? 'Carry-over line removed — the carry-over is waiting again.' : 'Adjustment deleted.');
      setTimeout(() => setDetailMsg(''), 4000);
      await Promise.all([loadDetail(detail.id), loadLists()]);
    } catch (e) {
      alert('Delete adjustment failed: ' + extractErrorMessage(e));
    }
  };

  // Signed money for adjustment line items (explicit + on positive; fmtMoney
  // already renders the minus on negatives).
  const fmtSigned = (v) => {
    const n = Number(v || 0);
    return (n > 0 ? '+' : '') + fmtMoney(n);
  };

  // ===========================================================================
  // Card for the two-section overview
  // ===========================================================================
  const renderBatchCard = (b) => {
    const sc = BATCH_STATUS_COLORS[b.status] || { bg: '#444', color: '#ccc' };
    return (
      <div key={b.id} onClick={() => openDetail(b.id)}
        style={{ background: panelBg, border: `1px solid ${borderColor}`, borderRadius: 6, padding: 14, cursor: 'pointer',
          display: 'grid', gridTemplateColumns: '60px 1fr 110px 80px 100px', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: textPrimary }}>#{b.id}</div>
        <div>
          <div style={{ fontSize: 12, color: textPrimary }}>
            {b.kind === 'renewal_bonus' && <span data-testid={`batch-card-renewal-${b.id}`} style={{ background: '#2d6a4f', color: '#fff', borderRadius: 4, padding: '1px 6px', fontSize: 11, fontWeight: 700, marginRight: 6 }}>{b.bonus_year} renewal bonus</span>}
            {b.kind === 'renewal_bonus' ? 'Sent' : 'Closed'} {fmtDateTime(b.closed_at)} <span style={{ color: textSecondary }}>{b.kind === 'renewal_bonus' ? 'for' : 'by'} {b.closed_by_name || `user #${b.closed_by_id}`}</span>
          </div>
          {b.paid_at && (
            <div style={{ fontSize: 11, color: textSecondary, marginTop: 2 }} data-testid={`batch-card-paid-${b.id}`}>
              Paid {fmtDateTime(b.paid_at)} by {b.paid_by_name || (b.paid_by_id ? `user #${b.paid_by_id}` : '—')} via {b.payment_method || '—'}
              {b.payment_reference ? ` · ${b.payment_reference}` : ''}
              {' · '}
              <span style={{ color: b.paying_company ? textPrimary : '#f5c97a' }}>{b.paying_company_name}</span>
            </div>
          )}
        </div>
        <div style={{ fontSize: 12, color: textPrimary, textAlign: 'right' }}>
          {b.kind === 'renewal_bonus' ? `${b.entry_count} client${b.entry_count === 1 ? '' : 's'}` : `${b.entry_count} entr${b.entry_count === 1 ? 'y' : 'ies'}`}
        </div>
        <div style={{ fontSize: 13, color: textPrimary, fontWeight: 600, textAlign: 'right' }}>
          {fmtMoney(b.total_amount)}
          {/* v5.49.0 paid more / less than owed */}
          {b.status === 'paid' && b.carryover && b.carryover.status !== 'cancelled' && (
            <div data-testid={`batch-card-paid-amount-${b.id}`} style={{ fontSize: 10, fontWeight: 400, color: '#f5c97a' }}>paid {fmtMoney(b.paid_amount)}</div>
          )}
        </div>
        <div style={{ textAlign: 'right' }}>
          <span data-testid={`batch-card-status-${b.id}`} title={Number(b.delayed_total || 0) > 0 ? batchPaidLabel(b) : undefined}
            style={{ padding: '3px 9px', borderRadius: 3, fontSize: 10, fontWeight: 600, background: sc.bg, color: sc.color, textTransform: 'uppercase' }}>
            {b.status}
          </span>
          {/* v5.45.0: a paid batch with delayed parts says how much is still to come */}
          {b.status === 'paid' && Number(b.delayed_total || 0) > 0 && (
            <div style={{ fontSize: 10, color: '#f5c97a', marginTop: 4 }}>{fmtMoney(b.delayed_total)} still to come</div>
          )}
        </div>
      </div>
    );
  };

  // v5.9.1: a one-time bonus as a display row in the paid list. It is NOT a
  // batch — clicking opens its own read-only detail modal locally (never via
  // selectedBatchId), so batch navigation is untouched.
  const renderOneOffCard = (o) => {
    const voided = !!o.is_voided;
    const strike = voided ? { textDecoration: 'line-through' } : {};
    const isCheck = o.payment_method === 'check';
    const ref = isCheck ? (o.check_number ? `#${o.check_number}` : '') : (o.zelle_note || '');
    const sc = voided ? STATUS_COLORS.voided : BATCH_STATUS_COLORS.paid;
    const nLines = (o.lines || []).length || 1;
    const expanded = expandedOneOffs.has(o.id);
    const toggleLines = (e) => {
      e.stopPropagation();
      setExpandedOneOffs(prev => {
        const next = new Set(prev);
        if (next.has(o.id)) next.delete(o.id); else next.add(o.id);
        return next;
      });
    };
    return (
      <div key={`oneoff-${o.id}`} onClick={() => setDetailOneOff(o)}
        style={{ background: panelBg, border: `1px solid ${borderColor}`, borderRadius: 6, padding: 14, cursor: 'pointer',
          display: 'grid', gridTemplateColumns: '60px 1fr 110px 80px 100px', alignItems: 'center', gap: 12, marginBottom: 8,
          opacity: voided ? 0.65 : 1 }}>
        <div style={{ fontSize: 16, color: '#f5c97a', textAlign: 'center' }} title="One-time bonus">★</div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 12, color: textPrimary, display: 'flex', gap: 8, alignItems: 'baseline', minWidth: 0 }}>
            <span style={{ padding: '1px 7px', borderRadius: 3, fontSize: 10, fontWeight: 700, background: '#3a2a14', color: '#f5c97a', whiteSpace: 'nowrap' }}>One-time bonus</span>
            <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{o.staff_name || `user #${o.staff_user_id}`}</span>
            <span style={{ color: textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', ...strike }} title={o.description}>{o.description}</span>
          </div>
          <div style={{ fontSize: 11, color: textSecondary, marginTop: 2 }}>
            Paid {fmtDate(o.paid_date)} · {o.paying_company_name} · {isCheck ? 'check' : 'zelle'}{ref ? ` · ${ref}` : ''}
          </div>
        </div>
        <div style={{ fontSize: 12, textAlign: 'right' }}>
          <span onClick={toggleLines} title={expanded ? 'Hide lines' : 'Show lines'}
            style={{ color: '#5BA3D9', cursor: 'pointer', fontWeight: 600 }}>
            {nLines} line{nLines === 1 ? '' : 's'} {expanded ? '▴' : '▸'}
          </span>
        </div>
        <div style={{ fontSize: 13, color: textPrimary, fontWeight: 600, textAlign: 'right', ...strike }}>
          {fmtMoney(o.amount)}
        </div>
        <div style={{ textAlign: 'right' }}>
          <span style={{ padding: '3px 9px', borderRadius: 3, fontSize: 10, fontWeight: 600, background: sc.bg, color: sc.color, textTransform: 'uppercase' }}>
            {voided ? 'voided' : 'paid'}
          </span>
        </div>
        {expanded && (
          <div style={{ gridColumn: '2 / -1', ...strike }} onClick={e => e.stopPropagation()}>
            <OneTimeBonusLines item={o} textPrimary={textPrimary} textSecondary={textSecondary} borderColor={borderColor} />
          </div>
        )}
      </div>
    );
  };

  // ===========================================================================
  // Detail view
  // ===========================================================================
  if (selectedBatchId && detail) {
    const isOpenBatch = detail.status === 'open';
    // v5.49.0 a regular batch may be paid more / less than it owes (reason required; the difference carries to the
    // employee's next payout); a renewal-bonus payout stays exact. A $0 batch is paid with amount 0 (nothing to Books).
    const regularBatch = detail.kind !== 'renewal_bonus';
    const owedC = toCents(detail.total_amount);
    const splitSt = splitState(splitRows, detail.total_amount, { allowDiff: regularBatch });
    const singleAmtC = regularBatch ? toCents(markPaidForm.amount) : owedC;
    const singleOk = !Number.isNaN(singleAmtC) && singleAmtC >= 0 && (singleAmtC > 0 || owedC === 0);
    const payDiffC = splitMode ? (splitSt.ready ? splitSt.diffC : 0) : (singleOk ? owedC - singleAmtC : 0);
    const reasonOk = !payDiffC || !!markPaidForm.reason.trim();
    const canMarkPaid = (splitMode ? splitSt.ready : (!!markPaidForm.paying_company && singleOk)) && reasonOk;
    // v4.6: adjustments render via a dedicated line-item template at the BOTTOM,
    // never through the normal entry row (which would show a blank-policy row).
    const allEntries = detail.entries || [];
    const realEntries = allEntries.filter(b => !b.is_adjustment);
    const adjEntries = allEntries.filter(b => b.is_adjustment);
    const ADJ_GRID = '1.4fr 1fr 90px 90px 80px 90px 90px 80px 90px 80px 90px 120px';
    const renderAdjRow = (b) => {
      if (editAdjId === b.id) {
        return (
          <div key={b.id}
            style={{ display: 'grid', gridTemplateColumns: ADJ_GRID, padding: '10px 12px', borderTop: `1px solid ${borderColor}`, background: darkMode ? '#23201a' : '#fffbf0', fontSize: 12, alignItems: 'center', gap: 6 }}>
            <span style={{ gridColumn: '1 / 9', display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 11, color: '#f5c97a', fontWeight: 700, whiteSpace: 'nowrap' }}>{b.carryover_id ? '↪ Carry-over' : '⚖ Adjustment'}</span>
              <input type="text" value={editAdjForm.note}
                placeholder="Note (required)"
                onChange={e => setEditAdjForm(f => ({ ...f, note: e.target.value }))}
                style={{ flex: 1, minWidth: 0, padding: '5px 7px', fontSize: 12, border: `1px solid ${borderColor}`, borderRadius: 4, background: bg, color: textPrimary, boxSizing: 'border-box' }} />
            </span>
            <span style={{ gridColumn: '9 / 10', textAlign: 'right' }}>
              <input type="number" step="0.01" value={editAdjForm.amount} data-testid={b.carryover_id ? `carry-edit-amount-${b.id}` : undefined}
                onChange={e => setEditAdjForm(f => ({ ...f, amount: e.target.value }))}
                style={{ width: '100%', padding: '5px 6px', fontSize: 12, border: `1px solid ${borderColor}`, borderRadius: 4, background: bg, color: textPrimary, boxSizing: 'border-box', textAlign: 'right' }} />
            </span>
            <span style={{ gridColumn: '10 / 13', display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
              <button onClick={() => submitEditAdjustment(b.id)} data-testid={b.carryover_id ? `carry-edit-save-${b.id}` : undefined}
                style={{ padding: '5px 12px', border: 'none', borderRadius: 4, background: '#185FA5', color: '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Save</button>
              <button onClick={() => setEditAdjId(null)}
                style={{ padding: '5px 12px', border: `1px solid ${borderColor}`, borderRadius: 4, background: panelBg, color: textSecondary, fontSize: 11, cursor: 'pointer' }}>Cancel</button>
            </span>
          </div>
        );
      }
      const net = Number(b.net_total_calculated || 0);
      const isCarry = !!b.carryover_id;                        // v5.49.0 carry-over line: admins edit / remove it
      const canManage = isOpenBatch && (isCarry ? isAdmin : isAdminOrManager);
      return (
        <div key={b.id} data-testid={isCarry ? `carry-line-${b.id}` : undefined}
          style={{ display: 'grid', gridTemplateColumns: ADJ_GRID, padding: '10px 12px', borderTop: `1px solid ${borderColor}`, background: darkMode ? '#201d18' : '#fffdf6', fontSize: 12, alignItems: 'center', gap: 6 }}>
          <span style={{ gridColumn: '1 / 9', color: textPrimary, display: 'flex', gap: 8, alignItems: 'baseline', minWidth: 0 }}>
            <span style={{ fontSize: 11, color: '#f5c97a', fontWeight: 700, whiteSpace: 'nowrap' }}>{isCarry ? '↪ Carry-over' : '⚖ Adjustment'}</span>
            <span style={{ color: textSecondary, overflow: 'hidden', textOverflow: 'ellipsis' }} title={b.notes || ''}>{b.notes || '—'}</span>
          </span>
          <span style={{ gridColumn: '9 / 10', color: net < 0 ? '#e0a8a8' : '#a8d8a8', textAlign: 'right', fontWeight: 600 }}>{fmtSigned(b.net_total_calculated)}</span>
          <span style={{ gridColumn: '10 / 11' }}>
            <span style={{ padding: '2px 8px', borderRadius: 3, fontSize: 10, fontWeight: 600, background: '#3a2a14', color: '#f5c97a', textTransform: 'uppercase' }}>{isCarry ? 'carry' : 'adj'}</span>
          </span>
          <span style={{ gridColumn: '11 / 12', color: textSecondary, fontSize: 11 }}>{fmtDate(b.created_at)}</span>
          <span style={{ gridColumn: '12 / 13', fontSize: 11, display: 'flex', gap: 8 }}>
            {canManage && (
              <>
                <button onClick={() => startEditAdj(b)} data-testid={isCarry ? `carry-edit-${b.id}` : undefined}
                  style={{ background: 'none', border: 'none', color: '#5BA3D9', fontSize: 11, cursor: 'pointer', padding: 0 }}>Edit</button>
                <button onClick={() => removeAdjustment(b.id, isCarry)} data-testid={isCarry ? `carry-remove-${b.id}` : undefined}
                  style={{ background: 'none', border: 'none', color: '#e0a8a8', fontSize: 11, cursor: 'pointer', padding: 0 }}>Delete</button>
              </>
            )}
          </span>
        </div>
      );
    };
    return (
      <div style={{ padding: 20, overflowY: 'auto', height: '100%', boxSizing: 'border-box' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
          <div>
            <button onClick={backToList}
              style={{ background: 'none', border: 'none', color: '#185FA5', fontSize: 12, cursor: 'pointer', padding: 0, marginBottom: 6 }}>
              ← Back to batches
            </button>
            <div style={{ fontSize: 18, fontWeight: 700, color: textPrimary }}>{detail.kind === 'renewal_bonus' ? `${detail.bonus_year} renewal bonus payout` : 'Payout batch'} #{detail.id}</div>
            <div style={{ fontSize: 12, color: textSecondary, marginTop: 3 }}>
              Closed {fmtDateTime(detail.closed_at)} by {detail.closed_by_name || `user #${detail.closed_by_id}`}
              {detail.paid_at && <> · Paid {fmtDateTime(detail.paid_at)} by {detail.paid_by_name || (detail.paid_by_id ? `user #${detail.paid_by_id}` : '—')}</>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <span data-testid="batch-status" style={{ padding: '4px 11px', borderRadius: 4, fontSize: 11, fontWeight: 600,
              background: (BATCH_STATUS_COLORS[detail.status] || {}).bg, color: (BATCH_STATUS_COLORS[detail.status] || {}).color,
              textTransform: detail.status === 'paid' ? 'none' : 'uppercase' }}>
              {detail.status === 'paid' ? batchPaidLabel(detail) : detail.status}
            </span>
            <button onClick={() => openPrintUrl(payoutBatches.printUrl(detail.id))}
              style={{ padding: '8px 14px', background: panelBg, color: textPrimary, border: `1px solid ${borderColor}`, borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              🖨 Print batch summary
            </button>
            {isAdmin && isOpenBatch && detail.kind === 'renewal_bonus' && !detail.finalized_at && (
              <span data-testid="renewal-not-finalized" style={{ fontSize: 12, color: '#f5c97a' }}>Finalize it first (HealthSherpa → Renewal bonus)</span>
            )}
            {isAdmin && isOpenBatch && !showMarkPaid && (detail.kind !== 'renewal_bonus' || detail.finalized_at) && (
              <button data-testid="mark-paid-open" onClick={() => { setMarkPaidForm({ ...EMPTY_MARK_PAID, amount: Number(detail.total_amount).toFixed(2) }); setSplitMode(false); setSplitRows([]); setShowMarkPaid(true); }}
                style={{ padding: '8px 18px', background: '#185FA5', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                Mark batch paid
              </button>
            )}
            {isAdminOrManager && isOpenBatch && !showAddAdj && detail.kind !== 'renewal_bonus' && (
              <button onClick={() => setShowAddAdj(true)}
                style={{ padding: '8px 16px', background: panelBg, color: '#f5c97a', border: `1px solid ${borderColor}`, borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                ⚖ Add adjustment
              </button>
            )}
          </div>
        </div>

        {/* Totals strip */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginBottom: 14 }}>
          <div style={{ background: panelBg, border: `1px solid ${borderColor}`, borderRadius: 6, padding: 12 }}>
            <div style={{ fontSize: 11, color: textSecondary }}>Entries</div>
            <div style={{ fontSize: 22, fontWeight: 600, color: textPrimary, marginTop: 3 }}>{detail.entry_count}</div>
          </div>
          <div style={{ background: panelBg, border: `1px solid ${borderColor}`, borderRadius: 6, padding: 12 }}>
            <div style={{ fontSize: 11, color: textSecondary }}>Total</div>
            <div style={{ fontSize: 22, fontWeight: 600, color: textPrimary, marginTop: 3 }}>{fmtMoney(detail.total_amount)}</div>
          </div>
          <div style={{ background: panelBg, border: `1px solid ${borderColor}`, borderRadius: 6, padding: 12 }}>
            <div style={{ fontSize: 11, color: textSecondary }}>Method</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: textPrimary, marginTop: 3 }}>{detail.payment_method || '—'}</div>
          </div>
          <div style={{ background: panelBg, border: `1px solid ${borderColor}`, borderRadius: 6, padding: 12 }}>
            <div style={{ fontSize: 11, color: textSecondary }}>Reference</div>
            <div style={{ fontSize: 14, color: textPrimary, marginTop: 3, wordBreak: 'break-word' }}>{detail.payment_reference || '—'}</div>
          </div>
          <div style={{ background: panelBg, border: `1px solid ${borderColor}`, borderRadius: 6, padding: 12 }}>
            <div style={{ fontSize: 11, color: textSecondary }}>Paying company</div>
            {/* v5.9.0: set at mark-paid; an open batch has none yet. */}
            <div style={{ fontSize: 14, fontWeight: 600, marginTop: 3, wordBreak: 'break-word',
              color: detail.status !== 'paid' ? textSecondary : (detail.paying_company ? textPrimary : '#f5c97a') }}>
              {detail.status !== 'paid' ? '—' : detail.paying_company_name}
            </div>
          </div>
        </div>

        {/* v5.45.0 the payment parts (who paid what; delayed parts; Edit split for admins) */}
        <PartsPanel batch={detail} isAdmin={isAdmin} t={{ bg, panelBg, borderColor, textPrimary, textSecondary }}
          onChanged={() => Promise.all([loadDetail(detail.id), loadLists()])} />
        {/* v5.49.0 paid more / less than owed: "Paid $X (owed $Y; -$D carried to next period)" */}
        <CarryInfo batch={detail} t={{ bg, panelBg, borderColor, textPrimary, textSecondary }} />

        {detail.notes && (
          <div style={{ padding: '8px 12px', background: panelBg, border: `1px solid ${borderColor}`, borderRadius: 5, fontSize: 12, color: textPrimary, marginBottom: 14 }}>
            <span style={{ color: textSecondary }}>Notes: </span>{detail.notes}
          </div>
        )}

        {/* v5.47.0 a HealthSherpa renewal-bonus payout: its client lines (no bonus entries) */}
        {detail.kind === 'renewal_bonus' && (
          <div data-testid="renewal-lines" style={{ padding: 12, background: panelBg, border: `1px solid ${borderColor}`, borderRadius: 6, marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: textSecondary, marginBottom: 6 }}>CLIENTS IN THIS RENEWAL BONUS</div>
            <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12, color: textPrimary }}>
              <thead><tr>{['Client', 'Carrier', 'Members', 'Per member', 'Amount', 'Status'].map((h) => <th key={h} style={{ textAlign: 'left', padding: '3px 6px', color: textSecondary, borderBottom: `1px solid ${borderColor}` }}>{h}</th>)}</tr></thead>
              <tbody>
                {(detail.renewal_lines || []).map((r) => (
                  <tr key={r.line_id}>
                    <td style={{ padding: '3px 6px' }}>{r.client}</td><td style={{ padding: '3px 6px' }}>{r.carrier || '—'}</td>
                    <td style={{ padding: '3px 6px' }}>{r.members}{r.member_names ? <span style={{ color: textSecondary }}> ({r.member_names.join(', ')})</span> : null}</td>
                    <td style={{ padding: '3px 6px' }}>{fmtMoney(r.per_member)}</td><td style={{ padding: '3px 6px', fontWeight: 600 }}>{fmtMoney(r.amount)}</td>
                    <td style={{ padding: '3px 6px' }}>{r.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {detailMsg && (
          <div style={{ padding: '8px 12px', background: '#1a3a1a', color: '#a8d8a8', borderRadius: 5, fontSize: 12, marginBottom: 14 }}>
            ✓ {detailMsg}
          </div>
        )}

        {/* Mark paid inline form */}
        {showMarkPaid && (
          <div style={{ padding: 14, marginBottom: 16, background: panelBg, border: `1px solid ${borderColor}`, borderRadius: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 10 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: textPrimary }}>Mark batch paid</span>
              <label style={{ fontSize: 12, color: textPrimary, display: 'flex', gap: 5, alignItems: 'center', cursor: 'pointer' }}>
                <input type="checkbox" data-testid="split-toggle" checked={splitMode}
                  onChange={e => {
                    const on = e.target.checked;
                    setSplitMode(on);
                    // split: method / reference become optional (Books records each part's check / Zelle)
                    setMarkPaidForm(f => ({ ...f, payment_method: on ? '' : (f.payment_method || 'check') }));
                    if (on && splitRows.length === 0) {
                      setSplitRows([{ ...blankPart(Number(detail.total_amount).toFixed(2)), paying_company: markPaidForm.paying_company || '' }]);
                    }
                  }} />
                Split payment
              </label>
              {splitMode && <span style={{ fontSize: 11, color: textSecondary }}>Paid by both companies, or partly later — one row per payment.</span>}
            </div>
            {splitMode && (
              <div style={{ marginBottom: 10 }}>
                <SplitEditor rows={splitRows} setRows={setSplitRows} total={detail.total_amount} allowDiff={regularBatch} t={{ bg, panelBg, borderColor, textPrimary, textSecondary }} />
              </div>
            )}
            {!splitMode && regularBatch && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: textSecondary, marginBottom: 3 }}>Amount paid <span style={{ color: '#e0a8a8' }}>*</span> <span>(owed {fmtMoney(detail.total_amount)})</span></div>
                <input type="text" inputMode="decimal" data-testid="mark-paid-amount" value={markPaidForm.amount}
                  onChange={e => setMarkPaidForm(f => ({ ...f, amount: e.target.value }))}
                  style={{ width: 160, padding: '6px 8px', fontSize: 12, textAlign: 'right', border: `1px solid ${singleOk ? borderColor : '#f5c97a'}`, borderRadius: 5, background: bg, color: textPrimary, boxSizing: 'border-box' }} />
                {owedC === 0 && <span data-testid="zero-batch-note" style={{ marginLeft: 10, fontSize: 11, color: textSecondary }}>This batch owes $0.00 — marking it paid posts nothing to Books.</span>}
              </div>
            )}
            {regularBatch && (
              <div style={{ marginBottom: 10 }}>
                <CarryNotice diffC={payDiffC} employee={detail.closed_by_name} reason={markPaidForm.reason}
                  setReason={(v) => setMarkPaidForm(f => ({ ...f, reason: v }))} t={{ bg, panelBg, borderColor, textPrimary, textSecondary }} />
              </div>
            )}
            {!splitMode && <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, color: textSecondary, marginBottom: 3 }}>Paying company <span style={{ color: '#e0a8a8' }}>*</span></div>
              <select value={markPaidForm.paying_company}
                onChange={e => setMarkPaidForm(f => ({ ...f, paying_company: e.target.value }))}
                style={{ width: '100%', maxWidth: 360, padding: '6px 8px', fontSize: 12, border: `1px solid ${markPaidForm.paying_company ? borderColor : '#f5c97a'}`, borderRadius: 5, background: bg, color: textPrimary, boxSizing: 'border-box' }}>
                <option value="" disabled>— Choose the paying company —</option>
                {PAYING_COMPANY_OPTIONS.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>}
            <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 10, marginBottom: 10 }}>
              <div>
                <div style={{ fontSize: 11, color: textSecondary, marginBottom: 3 }}>Payment method{splitMode ? ' (optional — Books records each check)' : ''}</div>
                <select value={markPaidForm.payment_method}
                  onChange={e => setMarkPaidForm(f => ({ ...f, payment_method: e.target.value }))}
                  style={{ width: '100%', padding: '6px 8px', fontSize: 12, border: `1px solid ${borderColor}`, borderRadius: 5, background: bg, color: textPrimary, boxSizing: 'border-box' }}>
                  {splitMode && <option value="">— not recorded —</option>}
                  {PAYMENT_METHODS.map(m => (
                    <option key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div>
                <div style={{ fontSize: 11, color: textSecondary, marginBottom: 3 }}>Reference (optional)</div>
                <input type="text" value={markPaidForm.payment_reference}
                  placeholder="Check #, transfer ID, etc."
                  onChange={e => setMarkPaidForm(f => ({ ...f, payment_reference: e.target.value }))}
                  style={{ width: '100%', padding: '6px 8px', fontSize: 12, border: `1px solid ${borderColor}`, borderRadius: 5, background: bg, color: textPrimary, boxSizing: 'border-box' }} />
              </div>
            </div>
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, color: textSecondary, marginBottom: 3 }}>Notes</div>
              <textarea value={markPaidForm.notes} rows={2}
                onChange={e => setMarkPaidForm(f => ({ ...f, notes: e.target.value }))}
                style={{ width: '100%', padding: '6px 8px', fontSize: 12, border: `1px solid ${borderColor}`, borderRadius: 5, background: bg, color: textPrimary, boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8 }}>
              {!splitMode && !markPaidForm.paying_company && (
                <span style={{ fontSize: 12, color: '#f5c97a', marginRight: 'auto' }}>Choose the paying company to continue.</span>
              )}
              {splitMode && !splitSt.ready && (
                <span style={{ fontSize: 12, color: '#f5c97a', marginRight: 'auto' }}>{regularBatch ? 'Complete every part (company and amount).' : `The parts must add up exactly to ${fmtMoney(detail.total_amount)}.`}</span>
              )}
              {!reasonOk && (splitMode ? splitSt.ready : singleOk) && (
                <span style={{ fontSize: 12, color: '#f5c97a', marginRight: 'auto' }}>Enter the reason for the difference.</span>
              )}
              <button onClick={() => setShowMarkPaid(false)}
                style={{ padding: '7px 16px', border: `1px solid ${borderColor}`, borderRadius: 5, background: panelBg, color: textSecondary, fontSize: 13, cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={submitMarkPaid} data-testid="mark-paid-confirm" disabled={markingPaid || !canMarkPaid}
                style={{ padding: '7px 16px', border: 'none', borderRadius: 5, fontSize: 13, fontWeight: 600,
                  background: canMarkPaid ? '#185FA5' : '#444', color: '#fff',
                  cursor: canMarkPaid && !markingPaid ? 'pointer' : 'not-allowed' }}>
                {markingPaid ? 'Saving…' : 'Confirm mark paid'}
              </button>
            </div>
          </div>
        )}

        {/* Add adjustment form (manager/admin, open batch only). Trigger button
            lives in the header action row; this panel mirrors the mark-paid form
            placement so neither collides with the floating Writer widget. */}
        {isAdminOrManager && isOpenBatch && showAddAdj && (
          <div style={{ padding: 14, marginBottom: 16, background: panelBg, border: `1px solid ${borderColor}`, borderRadius: 6 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: textPrimary, marginBottom: 10 }}>Add a batch adjustment</div>
            <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 10, marginBottom: 10 }}>
              <div>
                <div style={{ fontSize: 11, color: textSecondary, marginBottom: 3 }}>Signed amount</div>
                <input type="number" step="0.01" value={adjForm.amount}
                  placeholder="e.g. 50 or -25.50"
                  onChange={e => setAdjForm(f => ({ ...f, amount: e.target.value }))}
                  style={{ width: '100%', padding: '6px 8px', fontSize: 12, border: `1px solid ${borderColor}`, borderRadius: 5, background: bg, color: textPrimary, boxSizing: 'border-box' }} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: textSecondary, marginBottom: 3 }}>Note (required)</div>
                <input type="text" value={adjForm.note}
                  placeholder="Why this adjustment is being made"
                  onChange={e => setAdjForm(f => ({ ...f, note: e.target.value }))}
                  style={{ width: '100%', padding: '6px 8px', fontSize: 12, border: `1px solid ${borderColor}`, borderRadius: 5, background: bg, color: textPrimary, boxSizing: 'border-box' }} />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => { setShowAddAdj(false); setAdjForm({ amount: '', note: '' }); }}
                style={{ padding: '7px 16px', border: `1px solid ${borderColor}`, borderRadius: 5, background: panelBg, color: textSecondary, fontSize: 13, cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={submitAddAdjustment} disabled={savingAdj}
                style={{ padding: '7px 16px', border: 'none', borderRadius: 5, background: '#185FA5', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                {savingAdj ? 'Saving…' : 'Add adjustment'}
              </button>
            </div>
          </div>
        )}

        {/* Entries table */}
        <div style={{ border: `1px solid ${borderColor}`, borderRadius: 6, overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 90px 90px 80px 90px 90px 80px 90px 80px 90px 120px', padding: '8px 12px', background: panelBg, fontSize: 10, color: textSecondary, fontWeight: 600, textTransform: 'uppercase', alignItems: 'center', gap: 6 }}>
            <span>Customer</span>
            <span>Policy</span>
            <span>Effective</span>
            <span>Carrier</span>
            <span>Type</span>
            <span style={{ textAlign: 'right' }}>Premium</span>
            <span style={{ textAlign: 'right' }}>Bonus</span>
            <span style={{ textAlign: 'right' }}>Fee</span>
            <span style={{ textAlign: 'right' }}>Net</span>
            <span>Status</span>
            <span>Date</span>
            <span>Notes / actions</span>
          </div>
          {detailLoading ? (
            <div style={{ padding: 30, textAlign: 'center', color: textSecondary }}>Loading…</div>
          ) : allEntries.length === 0 ? (
            <div style={{ padding: 30, textAlign: 'center', color: textSecondary }}>
              No entries in this batch.
            </div>
          ) : (
            <>
              {realEntries.map(b => {
                const sc = STATUS_COLORS[b.status] || { bg: '#444', color: '#ccc' };
                return (
                  <div key={b.id}
                    style={{ display: 'grid', gridTemplateColumns: ADJ_GRID, padding: '10px 12px', borderTop: `1px solid ${borderColor}`, background: panelBg, fontSize: 12, alignItems: 'center', gap: 6 }}>
                    <span style={{ color: textPrimary }}>{b.customer_name || '—'}</span>
                    <span style={{ color: textPrimary, fontFamily: 'monospace', fontSize: 11 }}>{b.policy_number_raw}</span>
                    <span style={{ color: textSecondary }}>{fmtDate(b.effective_date)}</span>
                    <span style={{ color: textSecondary }}>{b.carrier || '—'}</span>
                    <span style={{ color: textSecondary }}>{b.policy_type || '—'}</span>
                    <span style={{ color: textPrimary, textAlign: 'right' }}>{fmtMoney(b.premium_amount)}</span>
                    <span style={{ color: textPrimary, textAlign: 'right' }}>{fmtMoney(b.bonus_amount_calculated)}</span>
                    <span style={{ color: textPrimary, textAlign: 'right' }}>{fmtMoney(b.policy_fee)}</span>
                    <span style={{ color: textPrimary, textAlign: 'right', fontWeight: 600 }}>{fmtMoney(b.net_total_calculated)}</span>
                    <span>
                      <span style={{ padding: '2px 8px', borderRadius: 3, fontSize: 10, fontWeight: 600, background: sc.bg, color: sc.color, textTransform: 'capitalize' }}>{b.status}</span>
                    </span>
                    <span style={{ color: textSecondary, fontSize: 11 }}>{fmtDate(b.paid_at || b.finalized_at || b.created_at)}</span>
                    <span style={{ fontSize: 11, color: textSecondary, display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {b.notes ? <span title={b.notes}>{b.notes.length > 30 ? b.notes.slice(0, 28) + '…' : b.notes}</span> : null}
                      {isAdminOrManager && isOpenBatch && (
                        <button onClick={() => returnEntryToDraft(b.id)}
                          style={{ background: 'none', border: 'none', color: '#f5c97a', fontSize: 11, cursor: 'pointer', padding: 0, textAlign: 'left' }}>
                          ↩ Return to draft
                        </button>
                      )}
                    </span>
                  </div>
                );
              })}
              {adjEntries.length > 0 && (
                <>
                  <div style={{ padding: '6px 12px', borderTop: `1px solid ${borderColor}`, background: darkMode ? '#252525' : panelBg, fontSize: 10, fontWeight: 600, color: textSecondary, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Adjustments ({adjEntries.length})
                  </div>
                  {adjEntries.map(renderAdjRow)}
                </>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  // ===========================================================================
  // Two-section overview (default view)
  // ===========================================================================
  // v5.9.1 paid list: batches + one-time bonuses, newest paid date first,
  // paged in the browser. Each one-time bonus counts as one item.
  const paidView = pageSlice(mergeTimeline(paidBatches, oneOffs), paidPage, PAID_PAGE_SIZE);
  const voidedOneOffs = oneOffs.filter(o => o.is_voided).length;
  const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
  const paidLabel = `${plural(paidBatches.length, 'paid batch', 'paid batches')} · `
    + `${plural(oneOffs.length, 'one-time bonus', 'one-time bonuses')}`
    + (isAdmin && voidedOneOffs > 0 ? ` (${voidedOneOffs} voided)` : '');

  return (
    <div style={{ padding: 20, overflowY: 'auto', height: '100%', boxSizing: 'border-box' }}>
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 18, fontWeight: 600, color: textPrimary }}>Payout batches</div>
        <div style={{ fontSize: 12, color: textSecondary, marginTop: 3 }}>
          {openBatches.length} open · click to review and mark paid
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 30, textAlign: 'center', color: textSecondary }}>Loading…</div>
      ) : (
        <>
          {/* Open */}
          <div style={{ fontSize: 11, fontWeight: 600, color: textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
            Open batches
          </div>
          {openBatches.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: textSecondary, background: panelBg, border: `1px solid ${borderColor}`, borderRadius: 6, marginBottom: 24 }}>
              No open batches. Staff create them via "Close out finalized" on the My bonuses tab.
            </div>
          ) : (
            <div style={{ marginBottom: 24 }}>
              {openBatches.map(renderBatchCard)}
            </div>
          )}

          {/* Archived */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Archived (paid)
              </div>
              <div style={{ fontSize: 11, color: textSecondary, marginTop: 2 }}>{paidLabel}</div>
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              {paidView.pages > 1 && (
                <span style={{ fontSize: 11, color: textSecondary }}>Page {paidView.page + 1} of {paidView.pages}</span>
              )}
              <button onClick={() => setPaidPage(paidView.page - 1)}
                disabled={!paidView.hasPrev}
                style={{ padding: '4px 10px', fontSize: 11, border: `1px solid ${borderColor}`, borderRadius: 4, background: panelBg, color: paidView.hasPrev ? textPrimary : textSecondary, cursor: paidView.hasPrev ? 'pointer' : 'not-allowed' }}>
                ← Prev
              </button>
              <button onClick={() => setPaidPage(paidView.page + 1)}
                disabled={!paidView.hasNext}
                style={{ padding: '4px 10px', fontSize: 11, border: `1px solid ${borderColor}`, borderRadius: 4, background: panelBg, color: paidView.hasNext ? textPrimary : textSecondary, cursor: paidView.hasNext ? 'pointer' : 'not-allowed' }}>
                Next →
              </button>
            </div>
          </div>
          {oneOffWarning && (
            <div style={{ padding: '6px 10px', marginBottom: 8, borderRadius: 5, fontSize: 11, background: darkMode ? '#3a2a14' : '#fff6e5', color: '#f5c97a' }}>
              ⚠ {oneOffWarning}
            </div>
          )}
          {paidView.total === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: textSecondary, background: panelBg, border: `1px solid ${borderColor}`, borderRadius: 6 }}>
              No archived batches.
            </div>
          ) : (
            <div>
              {paidView.rows.map(it => (it.kind === 'batch' ? renderBatchCard(it.data) : renderOneOffCard(it.data)))}
            </div>
          )}
        </>
      )}

      {detailOneOff && (
        <OneTimeBonusDetail item={detailOneOff} isAdmin={isAdmin} darkMode={darkMode}
          onClose={() => setDetailOneOff(null)}
          onVoided={() => { setDetailOneOff(null); loadLists(); }} />
      )}
    </div>
  );
}
